import React, { useState } from 'react';
import { Star, Trash2, Copy, BarChart3, Mail, MousePointerClick, Users, AlertCircle, ChevronDown, ChevronUp, RotateCcw, Clock, Send, PenSquare, Reply, ReplyAll } from 'lucide-react';
import type { MarketingEmailCampaign } from '../../../models/MasterAdmin/marketingEmail';
import { AUDIENCE_SEGMENT_LABELS } from '../../../models/MasterAdmin/marketingEmail';
import { meBadgeClass, meIconBtnClass, meMainPanelClass, mePanelHeaderClass, mePrimaryBtnClass, meSecondaryBtnClass } from './marketingEmailUi';
import EmailHtmlPreview from './EmailHtmlPreview';
import { prepareMarketingEmailHtmlForPreview } from './marketingEmailHtmlFormatter';

interface EmailReadPanelProps {
  email: MarketingEmailCampaign;
  onToggleStar: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit?: () => void;
  onRetrySend?: () => void;
  onRetrySchedule?: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  retrying?: boolean;
  replyLoading?: boolean;
}

function formatFullDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function RecipientList({ label, emails }: { label: string; emails: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (!emails.length) return null;

  const preview = emails.slice(0, 3).join(', ');
  const hasMore = emails.length > 3;

  return (
    <div className="mt-2">
      <div className="flex items-start gap-2">
        <span className="w-8 shrink-0 text-xs font-medium uppercase text-gray-400">{label}</span>
        <div className="min-w-0 flex-1">
          {expanded ? (
            <div className="flex flex-wrap gap-1.5">
              {emails.map((addr) => (
                <span
                  key={addr}
                  className="inline-flex rounded-full border border-blue-200/80 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/50 dark:text-blue-200"
                >
                  {addr}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {hasMore ? preview : emails.join(', ')}
              {hasMore && <span className="text-gray-400"> …and {emails.length - 3} more</span>}
            </p>
          )}
          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 flex items-center gap-0.5 text-xs text-primary hover:underline"
            >
              {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show all {emails.length} emails</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const EmailReadPanel: React.FC<EmailReadPanelProps> = ({
  email,
  onToggleStar,
  onDelete,
  onDuplicate,
  onEdit,
  onRetrySend,
  onRetrySchedule,
  onReply,
  onReplyAll,
  retrying = false,
  replyLoading = false,
}) => {
  const recipients = email.sentRecipients?.length ? email.sentRecipients : email.to;
  const effectiveDelivered = email.stats
    ? Math.max(
        Number(email.stats.delivered || 0),
        email.status === 'sent' ? Number(email.recipientCount || recipients.length || 0) : 0
      )
    : 0;
  const openRate = email.stats && effectiveDelivered > 0
    ? ((email.stats.opened / effectiveDelivered) * 100).toFixed(1) : null;

  return (
    <div className={meMainPanelClass}>
      <div className={mePanelHeaderClass}>
        <div className="flex min-w-0 items-center gap-2">
          <span className={meBadgeClass(email.status)}>{email.status}</span>
          {email.sendIndividually && !email.parentCampaignId && (
            <span className="rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
              Individual send
            </span>
          )}
          {email.parentCampaignId && (
            <span className="rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              Reply
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(onReply || onReplyAll) && (email.status === 'sent' || email.status === 'failed') && (
            <>
              {onReply && (
                <button
                  type="button"
                  onClick={onReply}
                  disabled={replyLoading}
                  className={meIconBtnClass}
                  title="Reply"
                >
                  <Reply className="h-4 w-4" />
                </button>
              )}
              {onReplyAll && (
                <button
                  type="button"
                  onClick={onReplyAll}
                  disabled={replyLoading}
                  className={meIconBtnClass}
                  title="Reply all"
                >
                  <ReplyAll className="h-4 w-4" />
                </button>
              )}
            </>
          )}
          <button type="button" onClick={onToggleStar} className={meIconBtnClass}>
            <Star className={`h-4 w-4 ${email.starred ? 'fill-amber-400 text-amber-400' : ''}`} />
          </button>
          <button type="button" onClick={onDuplicate} className={meIconBtnClass} title="Duplicate">
            <Copy className="h-4 w-4" />
          </button>
          <button type="button" onClick={onDelete} className={meIconBtnClass}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
          {email.subject || '(no subject)'}
        </h1>

        <div className="mt-4 rounded-xl border border-gray-200/80 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-950/50">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              V
            </div>
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-medium text-gray-900 dark:text-white">From: {email.from}</p>
              <p className="mt-0.5 text-xs text-gray-400">{formatFullDate(email.sentAt || email.scheduledAt || email.updatedAt)}</p>
            </div>
          </div>

          {recipients.length > 0 ? (
            <RecipientList label="To" emails={recipients} />
          ) : (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              To: {AUDIENCE_SEGMENT_LABELS[email.audienceSegment]}
            </p>
          )}

          <RecipientList label="Cc" emails={email.cc} />
          <RecipientList label="Bcc" emails={email.bcc} />

          {email.recipientMode === 'audience' && recipients.length > 0 && (
            <p className="mt-2 text-xs text-gray-400">
              Audience: {AUDIENCE_SEGMENT_LABELS[email.audienceSegment]} · {recipients.length} resolved recipient{recipients.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {email.status === 'scheduled' && email.scheduledAt && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Scheduled for {formatFullDate(email.scheduledAt)}
          </div>
        )}

        {email.status === 'failed' && (
          <div className="mt-4 rounded-xl border border-red-200/80 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium">Send failed</p>
                {email.sendError && (
                  <p className="mt-0.5 text-xs opacity-90 break-words">{email.sendError}</p>
                )}
                {email.scheduledAt && (
                  <p className="mt-1 text-xs opacity-80">
                    Was scheduled for {formatFullDate(email.scheduledAt)}
                  </p>
                )}
              </div>
            </div>
            {(onRetrySend || onRetrySchedule) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {onRetrySend && (
                  <button
                    type="button"
                    onClick={onRetrySend}
                    disabled={retrying}
                    className={mePrimaryBtnClass + ' !py-1.5 !text-xs'}
                  >
                    <Send className="h-3.5 w-3.5" />
                    {retrying ? 'Retrying…' : 'Retry send now'}
                  </button>
                )}
                {onRetrySchedule && (
                  <button
                    type="button"
                    onClick={onRetrySchedule}
                    disabled={retrying}
                    className={meSecondaryBtnClass + ' !py-1.5 !text-xs'}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    Reschedule
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {email.stats && (
          <div className="mt-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <BarChart3 className="h-4 w-4" />
              Performance
              {email.recipientCount ? (
                <span className="font-normal text-gray-400">· {email.recipientCount} recipients</span>
              ) : null}
              {openRate && <span className="font-normal text-gray-400">· {openRate}% opens</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[
                { label: 'Delivered', value: effectiveDelivered, icon: Mail },
                { label: 'Opened', value: email.stats.opened, icon: Users },
                { label: 'Clicked', value: email.stats.clicked, icon: MousePointerClick },
                { label: 'Bounced', value: email.stats.bounced, icon: AlertCircle },
                { label: 'Unsubscribed', value: email.stats.unsubscribed, icon: Users },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-gray-200/80 bg-white p-3 dark:border-gray-800 dark:bg-gray-950/50">
                  <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                    <Icon className="h-3 w-3" />
                    {label}
                  </div>
                  <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{value.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <EmailHtmlPreview
          html={prepareMarketingEmailHtmlForPreview(email.htmlBody)}
          subject={email.subject}
          from={email.from}
          minHeight="360px"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 px-6 py-3">
        {(email.status === 'sent' || email.status === 'failed') && onReplyAll && (
          <button
            type="button"
            onClick={onReplyAll}
            disabled={replyLoading}
            className={mePrimaryBtnClass}
          >
            <ReplyAll className="h-4 w-4" />
            {replyLoading ? 'Opening…' : 'Reply all'}
          </button>
        )}
        {(email.status === 'sent' || email.status === 'failed') && onReply && (
          <button
            type="button"
            onClick={onReply}
            disabled={replyLoading}
            className={meSecondaryBtnClass}
          >
            <Reply className="h-4 w-4" />
            Reply
          </button>
        )}
        {email.status === 'draft' && onEdit && (
          <button type="button" onClick={onEdit} className={mePrimaryBtnClass}>
            <PenSquare className="h-4 w-4" />
            Edit draft
          </button>
        )}
        {email.status === 'failed' && onRetrySend && (
          <button type="button" onClick={onRetrySend} disabled={retrying} className={mePrimaryBtnClass}>
            <RotateCcw className="h-4 w-4" />
            {retrying ? 'Retrying…' : 'Retry send'}
          </button>
        )}
        <button type="button" onClick={onDuplicate} className={meSecondaryBtnClass}>
          <Copy className="h-4 w-4" />
          Duplicate as new campaign
        </button>
      </div>
    </div>
  );
};

export default EmailReadPanel;
